// Pack walk planning: group chat + polls API wrappers.
import { API_URL } from '@/lib/api';

export type GroupChatMessage = {
  id: string;
  groupId: string;
  userId: string;
  senderName: string;
  type: 'text' | 'trail' | 'location' | 'poll';
  body: string | null;
  payload: { trail_id?: string; name?: string; lat?: number; lng?: number; label?: string; poll_id?: string; question?: string; options?: string[] };
  createdAt: string;
  seq: string | null;
  editedAt?: string | null;
  poll: {
    id: string;
    question: string;
    options: string[];
    isClosed: boolean;
    tally: Record<string, number>;
    totalVotes: number;
    /** multi-select: the caller's picked option indexes */
    myVotes?: number[];
    multiSelect?: boolean;
    createdBy: string;
  } | null;
};

export type GroupPollDto = GroupChatMessage['poll'] & { id: string };

export async function fetchGroupMessages(token: string, groupId: string, opts?: { beforeCreated?: string; beforeSeq?: string; limit?: number }): Promise<GroupChatMessage[]> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.beforeCreated) params.set('before_created', opts.beforeCreated);
  // seq arrives as a STRING on the wire — unique_rowid values exceed JS safe
  // integers, and rounding them as numbers corrupts the keyset cursor
  if (opts?.beforeSeq) params.set('before_seq', opts.beforeSeq);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const resp = await fetch(`${API_URL}/group/${groupId}/messages${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(await resp.text() || 'Failed to load chat');
  return resp.json();
}

export async function postGroupMessage(token: string, groupId: string, message: { type: string; body?: string | null; payload?: Record<string, any> }): Promise<GroupChatMessage> {
  const resp = await fetch(`${API_URL}/group/${groupId}/messages`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(message),
  });
  if (!resp.ok) throw new Error((await resp.text()) || 'Failed to send');
  return resp.json();
}

export async function deleteGroupMessage(token: string, groupId: string, messageId: string): Promise<void> {
  const resp = await fetch(`${API_URL}/group/${groupId}/messages/${messageId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error((await resp.text()) || 'Failed to delete message');
}

/** Toggle one option for the caller (multi-select — tap again to remove). */
export async function voteGroupPoll(token: string, groupId: string, pollId: string, optionIndex: number): Promise<GroupPollDto> {
  const resp = await fetch(`${API_URL}/group/${groupId}/messages/poll/${pollId}/vote`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ option_index: optionIndex }),
  });
  if (!resp.ok) throw new Error((await resp.text()) || 'Failed to vote');
  return resp.json();
}

export async function editGroupMessage(token: string, groupId: string, messageId: string, body: string, options?: string[]): Promise<GroupChatMessage> {
  const resp = await fetch(`${API_URL}/group/${groupId}/messages/${messageId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ body, ...(options ? { options } : {}) }),
  });
  if (!resp.ok) throw new Error((await resp.text()) || 'Failed to edit message');
  return resp.json();
}

export async function closeGroupPoll(token: string, groupId: string, pollId: string): Promise<GroupPollDto> {
  const resp = await fetch(`${API_URL}/group/${groupId}/messages/poll/${pollId}/close`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error((await resp.text()) || 'Failed to close poll');
  return resp.json();
}